import { MEDIA_LIMITS, QUALITY_PROFILES, SIMULCAST_LADDER } from '@cyphex/types';
import type { MediaPolicy } from '@cyphex/types/meetings';
import type { CallsApi } from './callsAdapter';
import type { IncomingCall } from './types';

const RING_TIMEOUT_MS = 30_000;

/** Offline demo only — the simulated media provider never dials this out. */
const MOCK_POLICY: MediaPolicy = {
  profile: 'standard',
  spec: QUALITY_PROFILES.standard,
  simulcast: true,
  publishLayers: [...SIMULCAST_LADDER],
  videoCodecs: ['vp8'],
  svc: false,
  dynacast: true,
  adaptiveStream: true,
  subscribeVisibleOnly: true,
  maxVideoSubscriptions: MEDIA_LIMITS.MAX_VIDEO_SUBSCRIPTIONS,
  maxAudioSubscriptions: MEDIA_LIMITS.MAX_AUDIO_SUBSCRIPTIONS,
  maxVisibleTiles: MEDIA_LIMITS.MAX_VISIBLE_TILES,
  townHall: false,
  loadState: 'green',
  degradationPreference: 'balanced',
};

const handlers = new Set<{
  onRing: (call: IncomingCall) => void;
  onCancel: (callId: string) => void;
}>();

let sequence = 0;

/** Builds the exact call shown on the reference popup. */
export function buildMockCall(now = Date.now()): IncomingCall {
  sequence += 1;
  return {
    callId: `call-${sequence}`,
    kind: 'video',
    isGroup: false,
    otherParticipantCount: 0,
    conversationId: 'c-charlotte',
    ringingSince: now,
    ringExpiresAt: now + RING_TIMEOUT_MS,
    isPriorityCaller: true,
  ringtone: 'default',
    caller: {
      id: 'u-charlotte',
      displayName: 'Charlotte De Crum',
      role: 'Product Lead',
    },
  };
}

/** Injection point used by the demo stream and by tests. */
export function emitMockRing(call: IncomingCall = buildMockCall()): void {
  for (const handler of handlers) handler.onRing(call);
}

export function emitMockCancel(callId: string): void {
  for (const handler of handlers) handler.onCancel(callId);
}

export const callsMock: CallsApi = {
  async accept(callId, withVideo) {
    return {
      callId,
      credentials: {
        url: 'wss://mock.invalid',
        token: 'mock',
        room: `c_${callId}`,
        identity: 'u-local',
        policy: MOCK_POLICY,
      },
      kind: withVideo ? 'video' : 'audio',
      peerName: 'Charlotte De Crum',
      peerId: 'u-charlotte',
      withVideo,
    };
  },
  async decline() {},
  async declineWithMessage() {},
  async listRinging() {
    return [];
  },
  subscribe(next) {
    handlers.add(next);
    return () => {
      handlers.delete(next);
    };
  },
};
